import { object } from "../util/ObjectUtil.js";
import { Language, LanguageCode, lang } from "./Language.js";
import { Text } from "./Text.js";

export type Label<LabelKey extends string = string> = LabelKey | LabelObject<LabelKey>;

export interface LabelObject<LabelKey extends string = string> {
    key: LabelKey;
    values?: { [key: string]: string };
    after?: string;
    before?: string;
    attrs?: string | string[];
}

export interface LabelStorage {
    [key: string]: string;
}

export interface DictionaryOptions {
    storage?: LabelStorage;
    language: Language;
}

export class Dictionary<LabelKey extends string = string> {

    public readonly language: Language;

    /**
     * The storage object for labels.
     */
    private _storage: LabelStorage = {};

    /**
     * Map to store the bindings between HTML elements and labels
     */
    private _bindings = new Map<WeakRef<HTMLElement>, Label<LabelKey>>();;

    /**
     * Represents a dictionary object.
     */
    constructor(options: DictionaryOptions) {
        this.language = options.language;

        if (options.storage) {
            this.add(options.storage);
        }

        this.language.onChange.add(
            () => {
                this.applyBindings();
            }
        );
    }

    /**
     * Adds labels to the dictionary storage.
     * 
     * @param storage The label storage to add.
     */
    public add(storage: LabelStorage) {
        this._storage = object.merge(this._storage, storage);
    }

    /**
     * Bind a label to an HTML element. When the language changes, the label will be updated.
     * 
     * @param element The HTML element to bind the label to.
     * @param key The label key.
     * @param language The language code to use. If not provided, the current language is used.
     * @returns A disposable object that can be used to unbind the label.
     */
    public bind(element: HTMLElement, key: Label<LabelKey>, language: LanguageCode = null): DisposableObject {
        let ref = new WeakRef(element);
        this._bindings.set(ref, key);
        this.apply(element, key, language);

        return {
            dispose: () => {
                this._bindings.delete(ref);
                ref = null;
            }
        }
    }

    /**
     * Applies the bindings to the elements in the dictionary.
     * If an element is no longer available, it will be removed from the dictionary.
     */
    private applyBindings(): void {
        this._bindings.forEach(
            (value, e) => {
                const element = e.deref();
                if (!element) {
                    this._bindings.delete(e);
                    return;
                }
                this.apply(element, value);
            }
        );
    }

    /**
     * Applies the label to the specified HTML element.
     * 
     * @param element The HTML element to apply the label to.
     * @param key The label key.
     * @param language The language code to use. If not provided, the current language is used.
     */
    public apply(element: HTMLElement, key: Label<LabelKey>, language: LanguageCode = null): void {
        let label = this.get(key, language);

        let attributes: string[] = null;
        if (this.isLabelObject(key)) {
            if (!key.attrs) {
                attributes = ["text"];
            } else if (Array.isArray(key.attrs)) {
                attributes = key.attrs;
            } else if (typeof key.attrs === "string") {
                attributes = [key.attrs];
            }
        } else {
            attributes = ["text"];
        }

        for (const attr of attributes) {
            if (!attr || attr === "text") {
                element.textContent = label;
            } else if (attr === "html") {
                element.innerHTML = label;
            } else if (attr.indexOf("@") === 0) {
                element.setAttribute(attr.substring(1), label);
            }
        }
    }

    /**
     * Escapes the label value and returns it as an HTML attribute value.
     * 
     * @param key The label key.
     * @param language The language code to use. If not provided, the current language is used.
     * @returns The escaped label value as an HTML attribute value.
     */
    public escAttr(key: Label<LabelKey>, language: LanguageCode = null): string {
        return Text.escapeAttr(this.get(key, language));
    }

    /**
     * Escapes the label value and returns it as an HTML string.
     * 
     * @param key The label key.
     * @param language The language code to use. If not provided, the current language is used.
     * @returns The escaped label value as an HTML string.
     */
    public escHtml(key: Label<LabelKey>, language: LanguageCode = null): string {
        return Text.escapeHtml(this.get(key, language));
    }

    /**
     * Retrieves the localized string value for the specified key and language.
     * If no language is provided, the current language is used.
     * If the key is a Label object, the before and after properties are concatenated with the localized value.
     * Any value placeholders in the result are replaced with the corresponding values from the Label object.
     * 
     * @param key The key or Label object to retrieve the localized value for.
     * @param language The language code to use for localization. If not provided, the current language is used.
     * @returns The localized string value.
     */
    public get(key: Label<LabelKey>, language: LanguageCode = null): string {

        if (!language) {
            language = this.language.current;
        }

        if (this.isLabelObject(key)) {
            let result = "";

            if (key.before) {
                result += key.before;
            }

            result += this.lookup(key.key, language);

            if (key.after) {
                result += key.after;
            }

            return this.replacePlaceholders(result, key.values);
        }

        return this.replacePlaceholders(this.lookup(key, language));
    }

    /**
     * Checks if the dictionary has a translation for the specified key and language.
     * If no language is provided, the current language of the dictionary is used.
     * 
     * @param key The key to check for translation.
     * @param language The language code to check for translation. If not provided, the current language of the dictionary is used.
     * @returns A boolean indicating whether the dictionary has a translation for the specified key and language.
     */
    public has(key: Label<LabelKey>, language: LanguageCode = null): boolean {
        if (!language) {
            language = this.language.current;
        }

        if (this.isLabelObject(key)) {
            return this._storage[`${language}.${key.key}`] !== undefined;
        }

        return this._storage[`${language}.${key}`] !== undefined;
    }

    /**
     * Looks up the localized value for the specified key and language.
     * If the value is not found, it falls back to the fallback languages.
     * If no value is found, it logs a warning and returns an empty string.
     * 
     * @param key The key to look up.
     * @param language The language code to use for localization.
     * @returns The localized value.
     */
    private lookup(key: LabelKey, language: LanguageCode): string {
        const fullKey = `${language}.${key}`;
        if (this._storage[fullKey] !== undefined) {
            return this._storage[fullKey];
        }

        const fallbacks = this.language.getFallbacks(language);

        for (const fallback of fallbacks) {
            const fullKey = `${fallback}.${key}`;
            if (this._storage[fullKey] !== undefined) {
                return this._storage[fullKey];
            }
        }

        console.warn(`Label not found: ${key}`);

        return "";
    }

    /**
     * Replaces placeholders in the given template with the corresponding values from the provided object.
     * 
     * @param template The string value with placeholders.
     * @param values The object containing the placeholder values.
     * @returns The string value with replaced placeholders.
     */
    private replacePlaceholders(template: string, values: { [key: string]: string } = null): string {
        if (!values) {
            return template;
        }

        return template.replace(/(\\)?(\{([^\{\}]+)\})/gm,
            //g1 = escape character
            //g2 = full match
            //g3 = key
            (match, g1, g2, g3) => {
                if (g1) {
                    return g2;
                }

                return values && values[g3] !== undefined ? values[g3] : '';
            }
        );
    }

    /**
     * Checks if the given label is a LabelObject.
     * 
     * @param label The label to check.
     * @returns True if the label is a LabelObject, false otherwise.
     */
    private isLabelObject(label: Label<LabelKey>): label is LabelObject<LabelKey> {
        if (!label) {
            return false;
        }

        if (typeof label === "string") {
            return false;
        }

        const type = typeof (label as LabelObject).key;
        return type === "string" || type === "object";
    }
}

export const dict = new Dictionary({
    language: lang
});